import { Button } from '@studio/design-system/button';
import type { HTMLAttributes, ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { failedRestores, retryFailedRestore, subscribeToFailedRestores } from '#~/agent/index.js';
import { Notice } from '#~/notifications/components/notice/index.js';
import { Sel, STagNew } from '#~/stag/index.js';

// ENG-3885: surfaces the persistent `failedRestores` map populated by
// broker.ts's `handleRestoreFailure` and renders a per-entry Retry
// button. Mounted alongside the existing notifications panel.
//
// The notification toast (raised via `raiseError` with `autoClose: false`)
// covers the "did something go wrong?" question for the user. THIS
// component covers "let me try again" — keeping the retry action close
// to the user's mental model of "those devices/recordings/destinations
// failed to come back when the app booted".
//
// Per rule: stay-in-scope (ai.knowledge/workflow) — app state stays outside design-system.

interface AgentRestoreFailureNoticeEntry {
  readonly id: string;
  readonly key: string;
  readonly kind: string;
  // C2: readable resource name carried on the registry entry by broker.ts.
  readonly name: string;
}

type Props = HTMLAttributes<HTMLDivElement>;

const RESTORE_KIND_LABEL: Record<string, string> = {
  snapshot: 'Sources',
  device: 'Device',
  recordingDirectory: 'Recording directory',
  rtmpDestinations: 'Streaming destinations'
};

const RESTORE_FAILURE_NOTICE_ID = 'agent-restore-failure-notice';

// ENG-3885: keys are formatted `${agentId}:${kind}:${id}` when the agent
// scope is available (the normal path from `broker.ts`), or `${kind}:${id}`
// as a fallback when `handleRestoreFailure` is called without an agentId.
// The 3-part form drops `agentId` for display — users don't think in
// agent IDs, they think in "the device named X failed to restore". When
// only 2 parts are present we treat them as `kind:id` for back-compat.
// C2: splitKey derives the id/kind/key from the map key only; the readable
// `name` is added by getRestoreFailureEntries from the stored entry, so this
// helper's result is the entry without that field.
const splitKey = (key: string): Omit<AgentRestoreFailureNoticeEntry, 'name'> => {
  const parts = key.split(':');

  if (parts.length >= 3) {
    return { key, kind: parts[1]!, id: parts.slice(2).join(':') };
  }

  if (parts.length === 2) {
    return { key, kind: parts[0]!, id: parts[1]! };
  }

  return { key, kind: key, id: '' };
};

const getRestoreFailureEntries = (): AgentRestoreFailureNoticeEntry[] =>
  [...failedRestores.entries()].map(([key, value]) => {
    const parsed = splitKey(key);
    // C2: prefer the readable name broker.ts stored on the entry; fall back to
    // the id parsed from the key so legacy/nameless entries still show something.
    return { ...parsed, name: value.name || parsed.id };
  });

export const AgentRestoreFailureNotice = ({ className, ...props }: Props): ReactElement | null => {
  const [entries, setEntries] = useState<AgentRestoreFailureNoticeEntry[]>(() =>
    getRestoreFailureEntries()
  );

  useEffect(
    () =>
      subscribeToFailedRestores(() => {
        setEntries(getRestoreFailureEntries());
      }),
    []
  );

  if (entries.length === 0) {
    return null;
  }

  const message = (
    <ul className='flex flex-col gap-8 text-sm'>
      {entries.map((entry) => {
        const kindLabel = RESTORE_KIND_LABEL[entry.kind] ?? entry.kind;
        // C2: show the saved resource's readable name. Suppress a redundant
        // suffix when the name already IS the kind label (recording directory /
        // streaming destinations) so we don't render "Recording directory: Recording directory".
        const showName = entry.name.length > 0 && entry.name !== kindLabel;

        return (
          <li
            className='flex items-start justify-between gap-12'
            data-testid={`restore-failure-${entry.key}`}
            key={entry.key}
          >
            <span className='min-w-0 break-words text-foreground'>
              <span className='font-demibold'>{kindLabel}</span>
              {showName ? `: ${entry.name}` : ''}
            </span>
            <Button
              aria-label={`Retry ${entry.name || kindLabel}`}
              className='focus-visible:ring-primary/50 shrink-0 focus-visible:ring'
              data-testid={`retry-${entry.key}`}
              onClick={() => {
                void retryFailedRestore(entry.key);
              }}
              size='sm'
              type='button'
              variant='secondary'
              {...STagNew(Sel.Notifications, 'restore_failure_retry', entry.key)}
            >
              Retry
            </Button>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className={className} data-testid='agent-restore-failure-notice' {...props}>
      <Notice
        closeable
        // Every failed item carries its own Retry button, so none may hide behind
        // Show more.
        collapsible={false}
        notification={{
          id: RESTORE_FAILURE_NOTICE_ID,
          message,
          severity: 'error',
          title: 'Restore failures'
        }}
        onClose={() => {
          // Keep failedRestores intact; closing only hides the retry toast surface.
          setEntries([]);
        }}
      />
    </div>
  );
};
